import { Comedian, getComedianByIDOrThrow } from "@/data/comedians";
import {
  ComedianWithImageUrl,
  FeedPost,
  FeedPostType,
} from "@/components/feed/types";
import { SpecialRow, SpecialWithComedian } from "@/data/specials";

import { CLIENT_getAllFutureShowsForComedian } from "@/data/shows";
import { ComedianRanks } from "@punchup/data/legacy";
import { SupabaseClient } from "@supabase/supabase-js";
import { genAbsoluteURL } from "@/components/common/utils";
import { getActiveAdCampaignsForLocation } from "@/data/adCampaigns";
import { getCDNImageURL } from "@/data/images";

const FEED_AD_UNIT_LOCATION_RADIUS_MILES = 50;

export function exponentialDecay(time: number, initial: number, rate: number) {
  return initial * Math.exp(-rate * time);
}

export const getComedian = ({
  id,
  filteredComedianDictionary,
}: {
  id: string | undefined;
  filteredComedianDictionary: { [comedianId: string]: Comedian };
}) => {
  if (!id) return undefined;
  return filteredComedianDictionary[id];
};

export const getCachedComedianRanks = async (): Promise<ComedianRanks[]> => {
  // Note: because we are fetching a static path at build time, we cannot query a path while it's being built (same host) so we need to use an
  // existing deployment, hence the punchup.live URL. In dev on the other hand you can use localhost:3000 is built or cached.
  const response = await fetch(genAbsoluteURL("/api/getComedianRanks"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(
      `Error getting cached comedian ranks: ${response.statusText}`
    );
  }
  const data = await response.json();
  return data.comedianRanks || [];
};

export { getCachedVideoRanks } from "@punchup/data/legacy/video";

export const getCachedPublishedComedians = async (): Promise<Comedian[]> => {
  // Note: because we are fetching a static path at build time, we cannot query a path while it's being built (same host) so we need to use an
  // existing deployment, hence the punchup.live URL. In dev on the other hand you can use localhost:3000 is built or cached.
  const response = await fetch(genAbsoluteURL("/api/published-comedians"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(
      `Error getting cached published comedians: ${response.statusText}`
    );
  }
  const data = await response.json();
  return data.publishedComedians || [];
};

export const getCachedPublishedSpecials = async (): Promise<SpecialRow[]> => {
  // Note: because we are fetching a static path at build time, we cannot query a path while it's being built (same host) so we need to use an
  // existing deployment, hence the punchup.live URL. In dev on the other hand you can use localhost:3000 is built or cached.
  const response = await fetch(genAbsoluteURL("/api/published-specials"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(
      `Error getting cached published specials: ${response.statusText}`
    );
  }
  const data = await response.json();
  return data.publishedSpecials || [];
};

export const getFeedAdObject = async ({
  supabase,
  latitude,
  longitude,
  filteredComedianDictionary,
}: {
  supabase: SupabaseClient;
  latitude: number;
  longitude: number;
  filteredComedianDictionary: { [comedianId: string]: Comedian };
}) => {
  // only continue if lat and long are available otherwise render feed
  if (latitude && longitude) {
    // get all currently active Feed ad campaigns within range of the user's location
    const feedAdCampaigns = await getActiveAdCampaignsForLocation(
      supabase,
      "feed",
      { latitude, longitude },
      FEED_AD_UNIT_LOCATION_RADIUS_MILES
    );

    // TODO: determine which campaign to render if there are multiple active campaigns
    // for a the user's location
    // for now, simply take the first one
    const feedAdCampaign = feedAdCampaigns[0];

    if (feedAdCampaign) {
      const { location_name, comedian_id } = feedAdCampaign;

      // get the comedian pertaining to the campaign
      const adComedian =
        getComedian({
          id: comedian_id,
          filteredComedianDictionary,
        }) ?? (await getComedianByIDOrThrow(comedian_id));

      if (adComedian) {
        // get the shows pertaining to the campaign
        const allShows = await CLIENT_getAllFutureShowsForComedian(
          adComedian.id
        );

        const shows = allShows
          .filter((show) => feedAdCampaign.show_ids.includes(show.id))
          .map((show) => ({
            ...show,
            datetime: show.datetime.toISOString(),
          }));

        const result = {
          comedianDisplayName: adComedian.display_name,
          comedianPageUrl: `/${adComedian.slug}`,
          locationName: location_name,
          shows,
        };

        return result;
      }
    }
  }

  // fallback to return null
  return null;
};

export const calculateSuggestedComedians = ({
  sortedUnfollowedComediansList,
  filteredComedianDictionary,
}: {
  sortedUnfollowedComediansList: Comedian[];
  filteredComedianDictionary: { [comedianId: string]: Comedian };
}): ComedianWithImageUrl[] =>
  sortedUnfollowedComediansList
    .map((comedian) => {
      const fullComedian = getComedian({
        id: comedian.id,
        filteredComedianDictionary,
      });

      if (fullComedian) {
        return {
          ...fullComedian,
          ...(comedian.about_img
            ? { imageUrl: getCDNImageURL(comedian.about_img) }
            : {}),
        };
      }
      return undefined;
    })
    .filter((comedian) => comedian !== undefined) as ComedianWithImageUrl[];

export function isComedianFollowed(
  comedianId: string,
  followedComedianIds: { comedian_id: string }[]
): boolean {
  return followedComedianIds.some((fc) => fc.comedian_id === comedianId);
}

export function hasValidRecommendations(feedPosts: FeedPost[]): boolean {
  // Exclude boosted content when checking for valid recommendations
  const nonBoostedPosts = feedPosts.filter((post) => post.score !== -1);

  if (nonBoostedPosts.length === 0) return false;

  const hasSpecials = nonBoostedPosts.some(
    (post) =>
      post.type === FeedPostType.SPECIAL ||
      post.type === FeedPostType.SUGGESTED_SPECIAL
  );
  const hasComedians = nonBoostedPosts.some(
    (post) => post.type === FeedPostType.COMEDIAN
  );

  return hasSpecials && hasComedians;
}

export function createComedianDictionary(allPublishedComedians: Comedian[]) {
  return allPublishedComedians.reduce(
    (acc, comedian) => {
      acc[comedian.id] = comedian;
      return acc;
    },
    {} as { [key: string]: Comedian }
  );
}

export function buildFilteredComedianDictionary(
  feedPosts: FeedPost[],
  publishedComediansDictionary: { [key: string]: Comedian }
) {
  const filteredComedianDictionary: Record<string, Comedian> = {};
  feedPosts.forEach((post) => {
    if (post.comedian) {
      if (!filteredComedianDictionary[post.comedian.id]) {
        filteredComedianDictionary[post.comedian.id] =
          publishedComediansDictionary[post.comedian.id];
      }
    } else if (post.special?.comedian_id) {
      if (!filteredComedianDictionary[post.special.comedian_id]) {
        filteredComedianDictionary[post.special.comedian_id] =
          publishedComediansDictionary[post.special.comedian_id];
      }
    }
  });
  return filteredComedianDictionary;
}

export const getCachedVerticalShorts = async (): Promise<
  SpecialWithComedian[]
> => {
  // Note: because we are fetching a static path at build time, we cannot query a path while it's being built (same host)
  // so we need to use an existing deployment, hence the genAbsoluteURL approach
  const response = await fetch(genAbsoluteURL("/api/getShortVideos"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(
      `Error getting cached vertical shorts: ${response.statusText}`
    );
  }
  const data = await response.json();
  return data.shorts || [];
};

export function deduplicateFeedPostsByEntity(posts: FeedPost[]): FeedPost[] {
  const seenComedians = new Set<string>();
  const seenSpecials = new Set<string>();
  const deduped: FeedPost[] = [];

  for (const post of posts) {
    if (post.type === FeedPostType.COMEDIAN && post.comedian) {
      if (!seenComedians.has(post.comedian.id)) {
        seenComedians.add(post.comedian.id);
        deduped.push(post);
      }
    } else if (
      (post.type === FeedPostType.SPECIAL ||
        post.type === FeedPostType.SUGGESTED_SPECIAL) &&
      post.special
    ) {
      if (!seenSpecials.has(post.special.id)) {
        seenSpecials.add(post.special.id);
        deduped.push(post);
      }
    } else {
      deduped.push(post);
    }
  }

  return deduped;
}

export function ensureCarouselComedianCoverage(
  dict: Record<string, Comedian>,
  specials: { comedian_id: string }[],
  source: Record<string, Comedian>
) {
  for (const sp of specials) {
    const cid = sp.comedian_id;
    if (cid && !dict[cid]) {
      const c = source[cid];
      if (c) dict[cid] = c;
    }
  }
}

/**
 * Re-orders the array that feeds the “Suggested Comedians” carousel so that:
 *   1. Boosted comedians stay at the very top
 *   2. Personalised-rank comedians come next (Snowflake order preserved)
 *   3. Everything else keeps its existing order
 *
 * NOTE: Mutates `list` in-place and returns void.
 */
export function prioritiseComediansForCarousel(
  list: { id: string }[], // e.g. orderedUnfollowedComedians
  boostedIds: Set<string>,
  personalisedPosts: FeedPost[] // sortedPersonalized
) {
  const personalRank = new Map<string, number>();
  personalisedPosts
    .filter((p) => p.type === FeedPostType.COMEDIAN && p.comedian)
    .forEach((p, idx) => {
      const cid = p.comedian!.id;
      if (!personalRank.has(cid)) personalRank.set(cid, idx);
    });

  list.sort((a, b) => {
    // boosted first
    const aBoost = boostedIds.has(a.id);
    const bBoost = boostedIds.has(b.id);
    if (aBoost && !bBoost) return -1;
    if (bBoost && !aBoost) return 1;

    // personalised next
    const aPers = personalRank.has(a.id);
    const bPers = personalRank.has(b.id);
    if (aPers && !bPers) return -1;
    if (bPers && !aPers) return 1;
    if (aPers && bPers) {
      return personalRank.get(a.id)! - personalRank.get(b.id)!;
    }

    return 0;
  });
}

export function seedCarouselWithBoosts(
  boostPosts: FeedPost[],
  output: Comedian[],
  seen: Set<string>,
  followed: Set<string>
) {
  boostPosts
    .filter((p) => p.type === FeedPostType.COMEDIAN)
    .map((p) => p.comedian!)
    .filter((c) => c && !seen.has(c.id) && !followed.has(c.id))
    .forEach((c) => {
      output.push(c);
      seen.add(c.id);
    });
}
